import { WidgetsRounded } from '@mui/icons-material';
import { ImportType, ImportError, ValidatedRow } from '../../api/importer.contracts';
import { ImportDefinition } from '../import-definition';
import { purple } from '@mui/material/colors';
import { AxiosProgressEvent } from 'axios';
import { importerService } from '../../api/importer.service';
import { ColDefOrGroup } from '@/lib/ag-grid/types';
import i18n from '@/lib/i18n/i18nConfig';
import { ApiResponse } from '@/lib/api/api-response';
import { isEmpty, isString } from 'lodash-es';

export interface AssetImportSummary {
  trackersToCreate: number;
  assetsToCreate: number;
  assetsWithTrackers: AssetTrackerDto[];
}

export interface AssetTrackerDto {
  isValid: boolean;
  errorReason?: ImportError;
  assetCode: string;
  assetTypeId: number;
  trackerBarcode?: string;
  trackerEPC?: string;
  trackerSigfoxDeviceId?: string;
}

export interface AssetImportRow extends ValidatedRow {
  assetCode: string;
  assetTypeId?: number;
  trackerBarcode?: string;
  trackerEPC?: string;
  trackerSigfoxDeviceId?: string;
}

export class AssetImport implements ImportDefinition<AssetImportSummary, AssetImportRow> {
  title = i18n.t('asset_import');
  description = i18n.t('import_assets_and_associate_them_with_trackers');
  icon = (<WidgetsRounded sx={{ fontSize: 40 }} />);
  iconColor = purple[500];
  type = ImportType.ASSET;
  templateFileUrl = '/importers/assets/asset_import_template.csv';

  async preview(data: { file: File; onUploadProgress: (progressEvent: AxiosProgressEvent) => void }) {
    const response = await importerService.validateFile<AssetImportSummary, AssetImportRow>(this.type, data.file, data.onUploadProgress);

    return response;
  }

  async import(payload: { summary: AssetImportSummary }): Promise<ApiResponse<boolean>> {
    const response = await importerService.importAssets(payload.summary.assetsWithTrackers);

    return response.processPayload((data) => response.isSuccess);
  }

  summaryColDefs: ColDefOrGroup<AssetImportSummary>[] = [
    {
      field: 'assetsToCreate',
      headerName: i18n.t('assets_to_create'),
    },
    {
      field: 'trackersToCreate',
      headerName: i18n.t('trackers_to_create'),
    },
  ];
  summaryMaxHeightInPx = 200;
  summaryMaxWidthInPx = 400;

  errorRowColDefs: ColDefOrGroup<AssetImportRow>[] = [
    {
      headerName: i18n.t('asset_code'),
      field: 'assetCode',
    },
    {
      field: 'isValid',
      headerName: i18n.t('valid'),
      cellDataType: 'text',
      valueFormatter: (params) => (params.value ? i18n.t('valid') : i18n.t('invalid')),
      cellClassRules: {
        'text-green-500': (params) => params.value,
        'text-red-500': (params) => !params.value,
      },
    },
    {
      headerName: i18n.t('error_reason'),
      field: 'errorReason',
      valueFormatter: (params) => {
        if (isEmpty(params.value) || !isString(params.value)) {
          return '';
        }

        return i18n.t(params.value.toLowerCase());
      },
    },
  ];
}
